// src/components/manager/booking/BookingCalendar.jsx
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Clock, User, Phone, Mail } from 'lucide-react';

export const BookingCalendar = ({ bookId, serviceId, onUpdate }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [viewMode, setViewMode] = useState('week'); // 'day', 'week', 'month'

  const timeSlots = Array.from({ length: 24 }, (_, i) => {
    const hour = i.toString().padStart(2, '0');
    return `${hour}:00`;
  });

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getWeekDates = (date) => {
    const week = [];
    const startDate = new Date(date);
    const day = startDate.getDay();
    
    startDate.setDate(startDate.getDate() - day);
    
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      week.push(currentDate);
    }
    
    return week;
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getBookingColor = (status) => {
    const colors = {
      confirmed: 'bg-green-100 border-green-300 text-green-800',
      pending: 'bg-yellow-100 border-yellow-300 text-yellow-800',
      cancelled: 'bg-red-100 border-red-300 text-red-800',
      completed: 'bg-blue-100 border-blue-300 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 border-gray-300 text-gray-800';
  };

  const weekDates = getWeekDates(currentDate);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Calendar Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-semibold text-gray-900">Booking Calendar</h3>
            
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              {['day', 'week', 'month'].map(mode => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-3 py-1 text-sm font-medium capitalize ${
                    viewMode === mode
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateWeek(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            
            <span className="text-lg font-semibold text-gray-900 min-w-[200px] text-center">
              {currentDate.toLocaleDateString('en-US', { 
                month: 'long', 
                year: 'numeric' 
              })}
            </span>
            
            <button
              onClick={() => navigateWeek(1)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            
            <button
              onClick={() => setCurrentDate(new Date())}
              className="ml-4 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
            >
              Today
            </button>
          </div>
        </div>
      </div>

      {/* Week View */}
      <div className="overflow-x-auto">
        <div className="min-w-full">
          {/* Day Headers */}
          <div className="grid grid-cols-8 border-b border-gray-200">
            <div className="p-3 text-sm font-medium text-gray-700 border-r border-gray-200">
              Time
            </div>
            {weekDates.map((date, index) => (
              <div 
                key={index}
                className={`p-3 text-center border-r border-gray-200 ${
                  date.toDateString() === new Date().toDateString()
                    ? 'bg-blue-50'
                    : ''
                }`}
              >
                <div className="text-sm font-medium text-gray-700">
                  {weekDays[date.getDay()]}
                </div>
                <div className={`text-lg font-semibold ${
                  date.toDateString() === new Date().toDateString()
                    ? 'text-blue-600'
                    : 'text-gray-900'
                }`}>
                  {date.getDate()}
                </div>
              </div>
            ))}
          </div>

          {/* Time Slots */}
          <div className="relative">
            {timeSlots.slice(8, 18).map((time, timeIndex) => (
              <div key={time} className="grid grid-cols-8 border-b border-gray-100 min-h-[60px]">
                <div className="p-3 text-sm text-gray-600 border-r border-gray-200 bg-gray-50">
                  {time}
                </div>
                {weekDates.map((date, dayIndex) => (
                  <div 
                    key={`${date.toISOString()}-${time}`}
                    className="relative border-r border-gray-200 p-1 hover:bg-gray-50"
                  >
                    {/* Render bookings for this time slot */}
                    {bookings
                      .filter(booking => {
                        const bookingDate = new Date(booking.date);
                        const bookingTime = formatTime(booking.date);
                        return bookingDate.toDateString() === date.toDateString() && 
                               bookingTime === time;
                      })
                      .map(booking => (
                        <div
                          key={booking.id}
                          onClick={() => setSelectedBooking(booking)}
                          className={`absolute inset-1 rounded p-1 text-xs cursor-pointer border ${
                            getBookingColor(booking.status)
                          }`}
                        >
                          <div className="font-medium truncate">
                            {booking.customerName}
                          </div>
                          <div className="truncate">
                            {booking.service}
                          </div>
                        </div>
                      ))
                    }
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Booking Details Modal */}
      {selectedBooking && (
        <BookingDetailsModal
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
          onUpdate={onUpdate}
        />
      )}
    </div>
  );
};

// Booking Details Modal
const BookingDetailsModal = ({ booking, onClose, onUpdate }) => {
  const handleStatusChange = async (newStatus) => {
    try {
      // await bookingService.updateBookingStatus(booking.id, newStatus);
      onUpdate?.();
    } catch (error) {
      console.error('Error updating booking status:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 p-6">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Booking Details</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ×
          </button>
        </div>

        <div className="space-y-4">
          {/* Customer Info */}
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Customer Information</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-500" />
                {booking.customerName}
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-gray-500" />
                {booking.email}
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-gray-500" />
                {booking.phone}
              </div>
            </div>
          </div>

          {/* Booking Info */}
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Booking Information</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-500" />
                {new Date(booking.date).toLocaleString()}
              </div>
              <div>Service: {booking.service}</div>
              <div>Duration: {booking.duration} minutes</div>
              {booking.notes && (
                <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                  <strong>Notes:</strong> {booking.notes}
                </div>
              )}
            </div>
          </div>

          {/* Status Actions */}
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Status</h4>
            <div className="flex gap-2">
              {['confirmed', 'pending', 'completed', 'cancelled'].map(status => (
                <button
                  key={status}
                  onClick={() => handleStatusChange(status)}
                  className={`px-3 py-1 rounded text-sm font-medium capitalize ${
                    booking.status === status
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
          <button className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Send Message
          </button>
        </div>
      </div>
    </div>
  );
};